import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Button,
  Badge,
  Select,
  FormField,
  Input,
  Textarea,
  ColumnLayout,
  Link,
  Alert
} from '@cloudscape-design/components';
import { useRole } from '../contexts/RoleContext';

interface ProductionOrder {
  orderId: string;
  userId: string;
  userEmail: string;
  designName: string;
  submissionDate: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  manufacturingFiles?: string[];
}

export const ProductionWorkQueue: React.FC = () => {
  const { isAdmin, isProduction, isLoading: roleLoading } = useRole();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('');
  const [newStatus, setNewStatus] = useState<string>('');
  const [productionNotes, setProductionNotes] = useState<string>('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadOrders();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, userFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      
      // TODO: Call API to load work queue
      // const response = await apiService.get('/api/production/work-queue', {
      //   params: { status: statusFilter, user: userFilter }
      // });
      
      // For now, use mock data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const mockOrders: ProductionOrder[] = [
        {
          orderId: 'ORD-003',
          userId: 'user-123',
          userEmail: 'john@example.com',
          designName: 'Detroit Sectional Map',
          submissionDate: '2026-03-11',
          status: 'pending'
        },
        {
          orderId: 'ORD-004',
          userId: 'user-456',
          userEmail: 'jane@example.com',
          designName: 'Green Bay Sectional',
          submissionDate: '2026-03-10',
          status: 'in_progress',
          notes: 'Started processing, estimated completion tomorrow'
        },
        {
          orderId: 'ORD-005',
          userId: 'user-789',
          userEmail: 'bob@example.com',
          designName: 'Chicago TAC',
          submissionDate: '2026-03-09',
          status: 'completed',
          notes: 'Completed successfully',
          manufacturingFiles: [
            'https://example.com/files/chicago-tac.tif',
            'https://example.com/files/chicago-tac.dxf'
          ]
        }
      ];
      
      // Apply filters
      let filtered = mockOrders;
      if (statusFilter !== 'all') {
        filtered = filtered.filter(o => o.status === statusFilter);
      }
      if (userFilter) {
        filtered = filtered.filter(o => 
          o.userEmail.toLowerCase().includes(userFilter.toLowerCase())
        );
      }
      
      setOrders(filtered);
    } catch (error) {
      console.error('Error loading work queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrder = (order: ProductionOrder) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setProductionNotes(order.notes || '');
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    
    try {
      setUpdating(true);
      
      // TODO: Call API to update order status
      // await apiService.put(`/api/production/orders/${selectedOrder.orderId}/status`, {
      //   status: newStatus,
      //   notes: productionNotes
      // });
      
      // For now, simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert('Order status updated successfully');
      await loadOrders();
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, 'blue' | 'green' | 'grey'> = {
      pending: 'blue',
      in_progress: 'blue',
      completed: 'green',
      cancelled: 'grey'
    };
    
    return (
      <Badge color={statusColors[status] || 'grey'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  // Check if user has permission
  if (!roleLoading && !isAdmin && !isProduction) {
    return (
      <Container>
        <Alert type="error">
          You do not have permission to access the production work queue.
        </Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Manage production orders and update their status"
      >
        Production Work Queue
      </Header>
      
      <Container>
        <SpaceBetween size="m">
          <ColumnLayout columns={3}>
            <FormField label="Status Filter">
              <Select
                selectedOption={{ label: statusFilter === 'all' ? 'All' : statusFilter, value: statusFilter }}
                onChange={({ detail }) => setStatusFilter(detail.selectedOption.value || 'all')}
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'Pending', value: 'pending' },
                  { label: 'In Progress', value: 'in_progress' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Cancelled', value: 'cancelled' }
                ]}
              />
            </FormField>
            
            <FormField label="User Filter">
              <Input
                value={userFilter}
                onChange={({ detail }) => setUserFilter(detail.value)}
                placeholder="Filter by email..."
              />
            </FormField>
            
            <Box padding={{ top: 'xxl' }}>
              <Button onClick={loadOrders} iconName="refresh">
                Refresh
              </Button>
            </Box>
          </ColumnLayout>
        </SpaceBetween>
      </Container>
      
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'orderId',
              header: 'Order ID',
              cell: (item: ProductionOrder) => (
                <Link onFollow={() => handleSelectOrder(item)}>
                  {item.orderId}
                </Link>
              )
            },
            {
              id: 'userEmail',
              header: 'User',
              cell: (item: ProductionOrder) => item.userEmail
            },
            {
              id: 'designName',
              header: 'Design Name',
              cell: (item: ProductionOrder) => item.designName
            },
            {
              id: 'submissionDate',
              header: 'Submission Date',
              cell: (item: ProductionOrder) => new Date(item.submissionDate).toLocaleDateString()
            },
            {
              id: 'status',
              header: 'Status',
              cell: (item: ProductionOrder) => getStatusBadge(item.status)
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item: ProductionOrder) => (
                <Button onClick={() => handleSelectOrder(item)}>
                  View Details
                </Button>
              )
            }
          ]}
          items={orders}
          loading={loading}
          loadingText="Loading work queue..."
          empty={
            <Box textAlign="center" color="inherit">
              <b>No orders</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No orders match the current filters.
              </Box>
            </Box>
          }
        />
      </Container>
      
      {selectedOrder && (
        <Container header={<Header variant="h2">Order Details</Header>}>
          <SpaceBetween size="l">
            <ColumnLayout columns={2}>
              <div>
                <Box variant="awsui-key-label">Order ID</Box>
                <div>{selectedOrder.orderId}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">User</Box>
                <div>{selectedOrder.userEmail}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Design Name</Box>
                <div>{selectedOrder.designName}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Submission Date</Box>
                <div>{new Date(selectedOrder.submissionDate).toLocaleDateString()}</div>
              </div>
            </ColumnLayout>
            
            <FormField label="Update Status">
              <Select
                selectedOption={{ label: newStatus, value: newStatus }}
                onChange={({ detail }) => setNewStatus(detail.selectedOption.value || '')}
                options={[
                  { label: 'Pending', value: 'pending' },
                  { label: 'In Progress', value: 'in_progress' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Cancelled', value: 'cancelled' }
                ]}
              />
            </FormField>
            
            <FormField label="Production Notes">
              <Textarea
                value={productionNotes}
                onChange={({ detail }) => setProductionNotes(detail.value)}
                placeholder="Add notes about the production status..."
                rows={4}
              />
            </FormField>
            
            {selectedOrder.manufacturingFiles && selectedOrder.manufacturingFiles.length > 0 && (
              <div>
                <Box variant="awsui-key-label">Manufacturing Files</Box>
                <SpaceBetween size="xs">
                  {selectedOrder.manufacturingFiles.map((file, index) => (
                    <Link key={index} external href={file}>
                      {file.split('/').pop()}
                    </Link>
                  ))}
                </SpaceBetween>
              </div>
            )}
            
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setSelectedOrder(null)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleUpdateStatus}
                loading={updating}
              >
                Update Status
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default ProductionWorkQueue;
